import { Component } from '@theme/component';

/**
 * IT PDP — the flyout's tab strip.
 *
 * Opening, closing, the scrim, Escape, focus trapping and scroll locking
 * all belong to the <theme-drawer> this sits inside; the size chart link
 * opens it with `on:click="#size-chart/open"`. This element only builds
 * the tabs from the panes' own labels, so adding a pane in the theme
 * editor is enough — there is no second list to keep in step.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tabs - The tab strip.
 * @property {HTMLElement} panes - The pane container.
 * @extends Component<Refs>
 */
class ItPdpFlyout extends Component {
  requiredRefs = ['tabs', 'panes'];

  connectedCallback() {
    super.connectedCallback();
    this.#buildTabs();
  }

  #panes() {
    return Array.from(this.refs.panes.querySelectorAll('.it-fly__pane'));
  }

  #buildTabs() {
    const panes = this.#panes();
    if (panes.length < 2) {
      // A single pane needs no tab strip; show it and leave the row empty
      // so the `:empty` rule collapses it.
      panes[0]?.classList.add('is-on');
      return;
    }

    this.refs.tabs.replaceChildren(
      ...panes.map((pane, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'it-fly__tab';
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', String(index === 0));
        tab.setAttribute('on:click', 'it-pdp-flyout/selectTab');
        tab.dataset.index = String(index);
        tab.textContent = pane.dataset.paneLabel || `Tab ${index + 1}`;

        pane.classList.toggle('is-on', index === 0);

        return tab;
      })
    );
  }

  /** @param {MouseEvent} event */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('.it-fly__tab') : null;
    if (!(tab instanceof HTMLElement)) return;

    const index = Number(tab.dataset.index);

    for (const other of this.refs.tabs.children) {
      other.setAttribute('aria-selected', String(other === tab));
    }

    this.#panes().forEach((pane, i) => pane.classList.toggle('is-on', i === index));
  }
}

if (!customElements.get('it-pdp-flyout')) {
  customElements.define('it-pdp-flyout', ItPdpFlyout);
}
